import { axiosApiService } from '../axios';

/**
 * MBF Dashboard API Service
 * Provides methods for fetching dashboard data for MBF Admin Dashboard.
 * These endpoints provide organization-wide data across ALL TinkerersLabs.
 * 
 * Query Parameters:
 * - start_date (optional): Start date for filtering (YYYY-MM-DD format)
 * - end_date (optional): End date for filtering (YYYY-MM-DD format)
 * - tl_id (optional): Filter by specific TinkerersLab ID
 */
const MBFDashboardAPIs = {
    /**
     * Get user statistics data across all TLs
     * @param {object} options - Query options
     * @param {string} options.startDate - Start date (YYYY-MM-DD)
     * @param {string} options.endDate - End date (YYYY-MM-DD)
     * @param {string} options.tlId - Optional TL ID filter
     * @returns {Promise<Object>} User statistics including demographics, growth trends
     */
    getUserStatistics: async function (options = {}) {
        try {
            const params = {};
            if (options.startDate) params.start_date = options.startDate;
            if (options.endDate) params.end_date = options.endDate;
            if (options.tlId) params.tl_id = options.tlId;

            const response = await axiosApiService.get('/stats/mbf-dashboard/user-statistics/', {
                params,
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching MBF user statistics:', error);
            throw error;
        }
    },

    /**
     * Get engagement data across all TLs
     * @param {object} options - Query options
     * @param {string} options.startDate - Start date (YYYY-MM-DD)
     * @param {string} options.endDate - End date (YYYY-MM-DD)
     * @param {string} options.tlId - Optional TL ID filter
     * @returns {Promise<Object>} Engagement data including peak hours, active users
     */
    getEngagement: async function (options = {}) {
        try {
            const params = {};
            if (options.startDate) params.start_date = options.startDate;
            if (options.endDate) params.end_date = options.endDate;
            if (options.tlId) params.tl_id = options.tlId;

            const response = await axiosApiService.get('/stats/mbf-dashboard/engagement/', {
                params,
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching MBF engagement data:', error);
            throw error;
        }
    },

    /**
     * Get projects data across all TLs
     * @param {object} options - Query options
     * @param {string} options.startDate - Start date (YYYY-MM-DD)
     * @param {string} options.endDate - End date (YYYY-MM-DD)
     * @param {string} options.tlId - Optional TL ID filter
     * @returns {Promise<Object>} Projects data including types, distributions
     */
    getProjects: async function (options = {}) {
        try {
            const params = {};
            if (options.startDate) params.start_date = options.startDate;
            if (options.endDate) params.end_date = options.endDate;
            if (options.tlId) params.tl_id = options.tlId;

            const response = await axiosApiService.get('/stats/mbf-dashboard/projects/', {
                params,
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching MBF projects data:', error);
            throw error;
        }
    },

    /**
     * Get machine usage data across all TLs
     * @param {object} options - Query options
     * @param {string} options.startDate - Start date (YYYY-MM-DD)
     * @param {string} options.endDate - End date (YYYY-MM-DD)
     * @param {string} options.tlId - Optional TL ID filter
     * @returns {Promise<Object>} Machine usage data including utilization, status
     */
    getMachineUsage: async function (options = {}) {
        try {
            const params = {};
            if (options.startDate) params.start_date = options.startDate;
            if (options.endDate) params.end_date = options.endDate;
            if (options.tlId) params.tl_id = options.tlId;
            if (options.oOOPeriod) params.oOOPeriod = options.oOOPeriod;

            const response = await axiosApiService.get('/stats/mbf-dashboard/machine-usage/', {
                params,
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching MBF machine usage data:', error);
            throw error;
        }
    },

    /**
     * Get MIS data across all TLs
     * @param {object} options - Query options
     * @param {string} options.startDate - Start date (YYYY-MM-DD)
     * @param {string} options.endDate - End date (YYYY-MM-DD)
     * @param {string} options.tlId - Optional TL ID filter
     * @returns {Promise<Object>} MIS data including issue types, resolution status
     */
    getMISData: async function (options = {}) {
        try {
            const params = {};
            if (options.startDate) params.start_date = options.startDate;
            if (options.endDate) params.end_date = options.endDate;
            if (options.tlId) params.tl_id = options.tlId;

            const response = await axiosApiService.get('/stats/mbf-dashboard/mis-data/', {
                params,
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching MBF MIS data:', error);
            throw error;
        }
    },

    /**
     * Get list of available TLs for dropdown
     * @returns {Promise<Array>} List of TLs with id and name
     */
    getTLList: async function () {
        try {
            const response = await axiosApiService.get('/auth/tinkerers_lab_list/');
            return response.data;
        } catch (error) {
            console.error('Error fetching TL list:', error);
            throw error;
        }
    },

    /**
     * Export dashboard data as CSV
     * @param {object} options - Query options
     * @param {string} options.dataType - 'user-statistics' | 'engagement' | 'projects' | 'machine-usage' | 'mis-data'
     * @param {string} options.startDate - Start date (YYYY-MM-DD)
     * @param {string} options.endDate - End date (YYYY-MM-DD)
     * @param {string} options.tlId - Optional TL ID filter
     * @returns {Promise<Blob>} CSV file blob for download
     */
    exportCSV: async function (options = {}) {
        try {
            const params = {};
            if (options.dataType) params.data_type = options.dataType;
            if (options.startDate) params.start_date = options.startDate;
            if (options.endDate) params.end_date = options.endDate;
            if (options.tlId) params.tl_id = options.tlId;

            const response = await axiosApiService.get('/stats/mbf-dashboard/export-csv/', {
                params,
                responseType: 'blob',
            });
            return response.data;
        } catch (error) {
            console.error('Error exporting MBF CSV:', error);
            throw error;
        }
    },
};

export default MBFDashboardAPIs;
